import { useEffect, useState } from "react";
import {
  getCorporateBranding,
  getCorporateContent,
  getCorporateLookupValues,
} from "../../../Services/corporate";

/**
 * Module-scoped cache of {branding, content, departments, roles} keyed by
 * `${slug}::${pageKey}`. Persists across in-app navigations within the same
 * tab so moving between Activation → Setup → Done for the same corporate
 * doesn't refetch (and doesn't flash a blank screen waiting for the network).
 * On page reload the cache resets, which is fine — first paint always pays
 * the network round-trip.
 */
const cache = new Map();
const cacheKeyOf = (slug, pageKey) => `${slug}::${pageKey}`;

/**
 * Loads the three datasets the activation page depends on, in parallel:
 *   1. branding (404 → notFound flag for the inactive-corporate fallback)
 *   2. published activation page content
 *   3. departments + roles lookup values (for the form dropdowns)
 *
 * Errors on (2)/(3) don't blow up the page — sections fall back to defaults
 * and lookup arrays default to []. Only branding 404 triggers the unavailable
 * fallback, since without branding we can't render the page chrome.
 *
 * Uses a stale-while-revalidate cache: on cache hit, returns immediately
 * with cached data and `loading=false`, then refetches in the background to
 * keep things fresh.
 *
 * @param {string} slug
 * @param {string} pageKey - usually "activation"
 */
export default function useCorporateBranding(slug, pageKey = "activation") {
  const cacheKey = cacheKeyOf(slug, pageKey);
  const cached = slug ? cache.get(cacheKey) : null;

  const [branding, setBranding] = useState(cached?.branding ?? null);
  const [content, setContent] = useState(cached?.content ?? null);
  const [departments, setDepartments] = useState(cached?.departments ?? []);
  const [roles, setRoles] = useState(cached?.roles ?? []);
  const [loading, setLoading] = useState(!cached);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!slug) return undefined;

    const hit = cache.get(cacheKey);
    if (hit) {
      // Hydrate immediately from cache so the page renders without flash.
      setBranding(hit.branding);
      setContent(hit.content);
      setDepartments(hit.departments);
      setRoles(hit.roles);
      setLoading(false);
    } else {
      setLoading(true);
    }
    setNotFound(false);

    (async () => {
      let brandingData;
      try {
        brandingData = await getCorporateBranding(slug);
        if (cancelled) return;
        setBranding(brandingData);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status || err?.status;
        if (status === 404) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        // Network/5xx — surface as not-found per spec (don't leak details)
        setNotFound(true);
        setLoading(false);
        return;
      }

      // Fire content + lookups in parallel; tolerate individual failures.
      const [contentResult, deptResult, roleResult] = await Promise.allSettled([
        getCorporateContent(slug, pageKey),
        getCorporateLookupValues(slug, "departments"),
        getCorporateLookupValues(slug, "roles"),
      ]);

      if (cancelled) return;

      const nextContent =
        contentResult.status === "fulfilled"
          ? contentResult.value
          : { sections: {} };
      const nextDepartments =
        deptResult.status === "fulfilled" ? deptResult.value?.items || [] : [];
      const nextRoles =
        roleResult.status === "fulfilled" ? roleResult.value?.items || [] : [];

      setContent(nextContent);
      setDepartments(nextDepartments);
      setRoles(nextRoles);
      setLoading(false);

      cache.set(cacheKey, {
        branding: brandingData,
        content: nextContent,
        departments: nextDepartments,
        roles: nextRoles,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [slug, pageKey, cacheKey]);

  return { branding, content, departments, roles, loading, notFound };
}
